import React from 'react';
import CopyField from './CopyField';
import {
  HOUSING_TYPES,
  KESIN_KAYIT,
  KURA_BILGI,
  KURA_ROZETLERI,
  PESINAT_PAYMENT_INFO,
  isKesinKayitOpen
} from './constants';

/**
 * Kura sonrası bilgilendirme kartı.
 * ASIL  → peşinat hesabı + kesin kayıt evrakları (son tarih vurgulu)
 * YEDEK → sıranın nasıl işleyeceği + hazırlanacak evraklar (ödeme yapılmaz)
 *
 * genel=true: sorgulama yapılmadan gösterilen genel bilgilendirme. Kişiye özel
 * peşinat tutarı bilinmediğinden tüm konut tiplerinin peşinatı listelenir.
 */
const KuraNextSteps = ({ sonuc, genel = false, pesinat, konutTipiMetni, showAlert }) => {
  const bilgi = KURA_BILGI[sonuc];
  if (!bilgi) return null;

  const asil = sonuc === 'ASIL';
  const sureDoldu = asil && !isKesinKayitOpen();
  const baslik = genel ? (bilgi.genelBaslik || bilgi.baslik) : bilgi.baslik;
  const ozet = genel ? (bilgi.genelOzet || bilgi.ozet) : bilgi.ozet;

  return (
    <div
      className={`rounded-2xl border-2 p-6 mb-6 ${asil
        ? 'bg-primary-light border-primary/25'
        : 'bg-sky-50 border-sky-200'
        }`}
    >
      {genel && (
        <span
          className={`inline-block mb-3 text-[11px] font-bold tracking-widest px-2.5 py-1 rounded-md text-white ${asil ? 'bg-emerald-600' : 'bg-sky-600'
            }`}
        >
          {KURA_ROZETLERI[sonuc]}
        </span>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className={`text-lg font-bold ${asil ? 'text-primary-text' : 'text-sky-900'}`}>
          {baslik}
        </h3>
        <span
          className={`ml-auto text-[11px] font-bold px-2.5 py-1 rounded-md text-white ${sureDoldu ? 'bg-red-600' : asil ? 'bg-primary' : 'bg-sky-600'
            }`}
        >
          {asil
            ? `${sureDoldu ? 'SON TARİH GEÇTİ' : 'SON TARİH'}: ${KESIN_KAYIT.sonTarih.toLocaleUpperCase('tr-TR')}`
            : 'BEKLEME LİSTESİ'}
        </span>
      </div>

      <p className={`text-sm leading-relaxed ${asil ? 'text-primary-text' : 'text-sky-900'}`}>
        {ozet}
      </p>

      {sureDoldu && (
        <p className="text-sm font-semibold text-red-800 bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
          Kesin kayıt için son tarih geçmiştir. Durumunuz hakkında bilgi almak için
          Karatay Belediyesi ile iletişime geçebilirsiniz.
        </p>
      )}

      {/* Peşinat bedeli ve hesap bilgileri yalnızca asil üyeleri ilgilendirir */}
      {asil && (
        <div className="mt-5">
          <div className="bg-white/70 rounded-lg px-4 py-3 mb-4 border border-primary/15">
            <span className="block text-xs text-gray-500 uppercase font-bold tracking-wider">
              {genel ? 'Peşinat Bedelleri' : 'Peşinat Bedeli'}
            </span>

            {genel ? (
              <ul className="divide-y divide-primary/10 mt-1">
                {HOUSING_TYPES.map((tip) => (
                  <li key={tip.kod} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-xs text-gray-600 leading-snug">
                      {tip.baslik} — {tip.konsept} ({tip.metrekare})
                    </span>
                    <span className="text-base font-bold text-primary whitespace-nowrap">{tip.pesinat}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-end justify-between gap-3 mt-1">
                <span className="text-xs text-gray-600 leading-snug">{konutTipiMetni || ''}</span>
                <span className="text-2xl font-bold text-primary whitespace-nowrap">{pesinat || '-'}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center py-2 border-b border-primary/10 mb-4">
            <span className="text-gray-700 font-medium">Banka:</span>
            <span className="text-sm font-bold text-gray-900">{PESINAT_PAYMENT_INFO.bankName}</span>
          </div>

          <CopyField
            label="Hesap Sahibi / Alıcı Adı"
            value={PESINAT_PAYMENT_INFO.accountHolder}
            successMessage="Hesap adı kopyalandı!"
            showAlert={showAlert}
            className="font-bold text-[10px] sm:text-xs tracking-tight text-center"
          />

          <CopyField
            label="Peşinat IBAN Numarası"
            value={PESINAT_PAYMENT_INFO.iban}
            copyValue={PESINAT_PAYMENT_INFO.iban.replace(/\s/g, '')}
            successMessage="IBAN kopyalandı!"
            showAlert={showAlert}
            className="font-mono text-sm tracking-tighter text-center"
          />
        </div>
      )}

      {/* Kesin kayıt evrakları */}
      <div className="mt-5">
        <p className={`text-xs uppercase font-bold tracking-wider mb-3 ${asil ? 'text-primary-text' : 'text-sky-900'}`}>
          {asil ? 'Kayıt İçin Gerekli Evraklar' : 'Sıra Gelince İstenecek Evraklar'}
        </p>
        <ul className="space-y-2">
          {KESIN_KAYIT.evraklar.map((evrak) => (
            <li key={evrak.baslik} className="flex items-start">
              <svg
                className={`w-5 h-5 mr-2 flex-shrink-0 ${asil ? 'text-primary' : 'text-sky-600'}`}
                fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-gray-800">
                <span className="font-semibold">{evrak.baslik}</span>
                {evrak.not && <span className="text-gray-600"> — {evrak.not}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Uyarılar */}
      <ul className={`mt-5 pt-4 border-t space-y-2 ${asil ? 'border-primary/15' : 'border-sky-200'}`}>
        {bilgi.uyarilar.map((uyari, index) => (
          <li key={index} className={`text-sm leading-relaxed flex items-start ${asil ? 'text-primary-text' : 'text-sky-900'}`}>
            <span className={`mt-1.5 mr-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${asil ? 'bg-primary' : 'bg-sky-600'}`}></span>
            {uyari}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default KuraNextSteps;
